// =============================================================================
// ESPOLIDAR WEB ADMIN - RESELLERS HOOKS
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { resellersApi } from '../services/api'
import type { ResellerCreate, ResellerUpdate } from '../types'
import toast from 'react-hot-toast'

// Lista resellers
export function useResellers() {
  return useQuery({
    queryKey: ['resellers'],
    queryFn: resellersApi.list,
  })
}

// Singolo reseller
export function useReseller(id: number) {
  return useQuery({
    queryKey: ['reseller', id],
    queryFn: () => resellersApi.get(id),
    enabled: !!id,
  })
}

// Crea reseller
export function useCreateReseller() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ResellerCreate) => resellersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      toast.success('Reseller creato')
    },
    onError: () => {
      toast.error('Errore creazione reseller')
    },
  })
}

// Aggiorna reseller
export function useUpdateReseller() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ResellerUpdate }) =>
      resellersApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      queryClient.invalidateQueries({ queryKey: ['reseller', id] })
      toast.success('Reseller aggiornato')
    },
    onError: () => {
      toast.error('Errore aggiornamento reseller')
    },
  })
}

// Elimina reseller
export function useDeleteReseller() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resellersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      toast.success('Reseller eliminato')
    },
    onError: () => {
      toast.error('Errore eliminazione reseller')
    },
  })
}

// Assegna dispositivo
export function useAssignDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resellerId, deviceId }: { resellerId: number; deviceId: string }) =>
      resellersApi.assignDevice(resellerId, deviceId),
    onSuccess: (_, { resellerId }) => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      queryClient.invalidateQueries({ queryKey: ['reseller', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['reseller-devices', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      toast.success('Dispositivo assegnato')
    },
    onError: () => {
      toast.error('Errore assegnazione dispositivo')
    },
  })
}

// Rimuovi dispositivo
export function useUnassignDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resellerId, deviceId }: { resellerId: number; deviceId: string }) =>
      resellersApi.unassignDevice(resellerId, deviceId),
    onSuccess: (_, { resellerId }) => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      queryClient.invalidateQueries({ queryKey: ['reseller', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['reseller-devices', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      toast.success('Dispositivo rimosso')
    },
    onError: () => {
      toast.error('Errore rimozione dispositivo')
    },
  })
}

// Assegna utente
export function useAssignUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resellerId, userId }: { resellerId: number; userId: number }) =>
      resellersApi.assignUser(resellerId, userId),
    onSuccess: (_, { resellerId }) => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      queryClient.invalidateQueries({ queryKey: ['reseller', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['reseller-users', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Utente assegnato')
    },
    onError: () => {
      toast.error('Errore assegnazione utente')
    },
  })
}

// Rimuovi utente
export function useUnassignUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resellerId, userId }: { resellerId: number; userId: number }) =>
      resellersApi.unassignUser(resellerId, userId),
    onSuccess: (_, { resellerId }) => {
      queryClient.invalidateQueries({ queryKey: ['resellers'] })
      queryClient.invalidateQueries({ queryKey: ['reseller', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['reseller-users', resellerId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Utente rimosso')
    },
    onError: () => {
      toast.error('Errore rimozione utente')
    },
  })
}

// Dispositivi di un reseller
export function useResellerDevices(resellerId: number) {
  return useQuery({
    queryKey: ['reseller-devices', resellerId],
    queryFn: () => resellersApi.getDevices(resellerId),
    enabled: !!resellerId,
  })
}

// Utenti di un reseller
export function useResellerUsers(resellerId: number) {
  return useQuery({
    queryKey: ['reseller-users', resellerId],
    queryFn: () => resellersApi.getUsers(resellerId),
    enabled: !!resellerId,
  })
}
